import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Sizes/variants follow concept2 prototype (docs/studio/design-concept2/src/primitives.jsx Card).
// Base: border-radius var(--radius), text-card-foreground.
// Variants:
//   default  — bg-card + border
//   elevated — bg-card + border + shadow
//   outline  — transparent + border
//   ghost    — transparent + no border
//
// Sub-components (CardHeader/Content/Footer) are simple padding wrappers,
// composable via children. Footer adds horizontal flex with gap.
const cardVariants = cva(
  "rounded-lg text-card-foreground",
  {
    variants: {
      variant: {
        default: "bg-card border border-border",
        elevated: "bg-card border border-border shadow-md",
        outline: "bg-transparent border border-border",
        ghost: "bg-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant }), className)} {...props} />
  )
)
Card.displayName = "Card"

// Sub-components — concept2 padding values:
//   Header  20/20/12 px → pt-5 px-5 pb-3
//   Content 12/20    px → px-5 py-3
//   Footer  12/20/20 px → pt-3 px-5 pb-5 + flex items-center gap-2

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("px-5 pb-3 pt-5", className)} {...props} />
  )
)
CardHeader.displayName = "CardHeader"

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("px-5 py-3", className)} {...props} />
  )
)
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("flex items-center gap-2 px-5 pb-5 pt-3", className)}
      {...props}
    />
  )
)
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardContent, CardFooter, cardVariants }
